import { useEffect, useLayoutEffect, useRef } from "react";
import { useReviewComments } from "../api/hooks/useReview";
import {
  Outlet,
  useLocation,
  useParams,
} from "react-router-dom";
import {
  useProject,
  useProjectAgentStatus,
  useUploadsList,
} from "../api";
import { DEFAULT_SLOT } from "../api/agentSlot";
import { AgentSlotProvider } from "../api/AgentSlotProvider";
import { useProjectAgentConfigs } from "../api/hooks/useAgentConfigs";
import { isStoodDown } from "../utils/standDown";
import {
  type AgentPromptSummary,
  useStatefulAgents,
} from "../api/hooks/useStatefulAgents";
import { AgentSlotBanner, AgentSlotSwitcher } from "../components/AgentSlotBar";
import { slotAvailable } from "../utils/agentSlotText";
import { LastRun } from "../components/LastRun";
import { RerunNowButton } from "../components/RerunNowButton";
import { TabRows } from "../components/TabRows";
import { PageHeader } from "../components/PageHeader";
import { pageTitleClassName } from "../components/pageTitleClassName";
import { ProjectTitle } from "../components/ProjectTitle";
import { Tooltip } from "../components/Tooltip";
import { adminSiteUrl } from "../utils/adminSite";
import { projectDisplayName } from "../utils/projectName";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import styles from "./ProjectHubLayout.module.css";
import { SLOT_PARAM, useProjectSlot } from "./useProjectSlot";

type TabId =
  | "learning-agents/conversations"
  | "learning-agents/scheduled"
  | "learning-agents/state"
  | "status"
  | "actions"
  | "status/attributes"
  | "status/graph"
  // A specialist's file on the project.
  | `status/${string}`
  | "files"
  | "procurement"
  | "settings";

interface Tab {
  to: TabId;
  label: string;
  // Starts a new group in the tab strip, after a divider.
  divider?: boolean;
  debug?: boolean;
  // An optional specialist's tab: shown only while it is switched on for the project.
  agent?: string;
}

interface TabGroup {
  id: string;
  label: string;
  // Selecting the group lands on tabs[0]; a single-tab group renders no second row.
  tabs: readonly [Tab, ...Tab[]];
}

// Two levels: agent fleet / project-wide surface, then that group's tabs. Every
// ``to`` is a stable route segment, so links and bookmarks keep resolving.
const TAB_GROUPS: readonly TabGroup[] = [
  // Where the project stands, across every agent working it - a peer of the
  // agent fleet rather than a tab inside it, and the project's landing group.
  {
    id: "status",
    label: "Status",
    tabs: [
      { to: "status", label: "Overview" },
      // Every specialist's actions in one table; outside status/ so it is not read as one.
      { to: "actions", label: "All actions" },
      // The specialist files Overview links through to go here (withSpecialistTabs).
      { to: "status/attributes", label: "Debug: Attributes", debug: true },
      { to: "status/graph", label: "Debug: Graph", debug: true },
    ],
  },
  {
    id: "learning-agents",
    label: "Conversations",
    tabs: [
      // Mirrors the project team's own tabs, so the two read the same way.
      { to: "learning-agents/conversations", label: "Conversations" },
      { to: "learning-agents/scheduled", label: "Debug: Scheduled", debug: true },
      { to: "learning-agents/state", label: "Debug: State", debug: true },
    ],
  },
  {
    id: "procurement",
    label: "Procurement",
    tabs: [{ to: "procurement", label: "Procurement" }],
  },
  { id: "files", label: "Files", tabs: [{ to: "files", label: "Files" }] },
  {
    id: "settings",
    label: "Settings",
    tabs: [{ to: "settings", label: "Settings" }],
  },
];

interface ResolvedTab {
  group: TabGroup;
  tab: Tab;
}

// The Status group gains a tab per specialist that keeps a status row, in team order,
// before its debug tabs: the files Overview links through to.
const withSpecialistTabs = (roster: AgentPromptSummary[]): readonly TabGroup[] =>
  TAB_GROUPS.map((group) => {
    if (group.id !== "status") return group;
    const [first, ...rest] = group.tabs;
    const firstDebug = rest.findIndex((t) => t.debug);
    const at = firstDebug === -1 ? rest.length : firstDebug;
    const specialistTabs: Tab[] = roster
      .filter((a) => !a.is_manager && a.keeps_status_row)
      .map((a, i) => ({
        to: `status/${a.agent}`,
        label: a.label,
        divider: i === 0,
        ...(a.optional ? { agent: a.agent } : {}),
      }));
    return {
      ...group,
      tabs: [first, ...rest.slice(0, at), ...specialistTabs, ...rest.slice(at)],
    };
  });

// Right-to-left so a deep link resolves to its parent tab, not its trailing id.
// Nested tabs are matched across the whole path first, so a trailing segment that
// happens to name a tab (learning-agents/state/grid) does not win over its parent.
// No match falls back to the first tab, Status - which is what the index route
// renders, so the highlighted tab and the landing route cannot disagree.
const agentOfTab = (t: Tab): string | null =>
  t.to.startsWith("status/") && !t.debug ? t.to.slice("status/".length) : null;

// A specialist not involved in the project keeps its tab, marked "(N/A)", after those
// working it and before the debug tabs; the divider stays on whichever agent tab now
// opens the group.
const agentsNotInvolvedLast = (
  tabs: readonly Tab[],
  notInvolved: ReadonlySet<string>,
): Tab[] => {
  const isMoved = (t: Tab) => notInvolved.has(agentOfTab(t) ?? "");
  const moved = tabs.filter(isMoved).map((t) => ({ ...t, label: `${t.label} (N/A)` }));
  const rest = tabs.filter((t) => !isMoved(t));
  const firstDebug = rest.findIndex((t) => t.debug);
  const at = firstDebug === -1 ? rest.length : firstDebug;
  const ordered = [...rest.slice(0, at), ...moved, ...rest.slice(at)];
  const firstAgent = ordered.findIndex((t) => agentOfTab(t) !== null);
  return ordered.map((t, i) => (agentOfTab(t) ? { ...t, divider: i === firstAgent } : t));
};

const resolveTab = (
  pathname: string,
  groups: readonly TabGroup[],
): ResolvedTab => {
  const ALL_TABS: readonly ResolvedTab[] = groups.flatMap((group) =>
    group.tabs.map((tab) => ({ group, tab })),
  );
  const segments = pathname.replace(/\/+$/, "").split("/");
  for (let i = segments.length - 1; i > 0; i--) {
    const pair = `${segments[i - 1]}/${segments[i]}`;
    const nested = ALL_TABS.find((t) => t.tab.to === pair);
    if (nested) return nested;
  }
  for (let i = segments.length - 1; i >= 0; i--) {
    const hit = ALL_TABS.find((t) => t.tab.to === segments[i]);
    if (hit) return hit;
  }
  return ALL_TABS[0];
};

// How far down the window the pinned name and tabs reach, as --pinned-top, so what sticks
// or scrolls under them (the graph's detail, a conversation's New divider) clears them.
// 0 on a phone, where the band scrolls away.
const usePublishPinnedTop = () => {
  const band = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = band.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () => {
      const style = getComputedStyle(el);
      const covers =
        style.position === "sticky"
          ? el.getBoundingClientRect().height + parseFloat(style.top)
          : 0;
      root.style.setProperty("--pinned-top", `${Math.max(0, Math.round(covers))}px`);
    };
    publish();
    const grows = new ResizeObserver(publish);
    grows.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      grows.disconnect();
      window.removeEventListener("resize", publish);
      root.style.removeProperty("--pinned-top");
    };
  }, []);
  return band;
};

// Everything under the hub reads and writes the slot in view; a switch remounts it, so
// no page keeps state from the slot it left.
export const ProjectHubLayout = () => {
  const { projectId } = useParams();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  const { slot, choose } = useProjectSlot(decoded);
  return (
    <AgentSlotProvider key={slot} projectId={decoded} slot={slot}>
      <ProjectHub projectId={decoded} slot={slot} chooseSlot={choose} />
    </AgentSlotProvider>
  );
};

const ProjectHub = ({
  projectId: decoded,
  slot,
  chooseSlot,
}: {
  projectId: string;
  slot: string;
  chooseSlot: ReturnType<typeof useProjectSlot>["choose"];
}) => {
  const pinnedBand = usePublishPinnedTop();
  // A slot this environment may not run is only in Settings, to remove.
  const slots = useProjectAgentConfigs(decoded).data?.slots.filter(slotAvailable);
  const slotInView = slots?.find((s) => s.slot === slot);
  // A link to a slot since removed, or not runnable here, lands on default rather than on
  // an error.
  useEffect(() => {
    if (slots && !slotInView) chooseSlot(DEFAULT_SLOT, { replace: true });
  }, [slots, slotInView, chooseSlot]);
  const slotSearch = slot === DEFAULT_SLOT ? "" : `?${SLOT_PARAM}=${encodeURIComponent(slot)}`;
  const { data: project } = useProject(decoded);
  // Falls back to the id until the project loads.
  useDocumentTitle(
    project ? projectDisplayName(project) : decoded || undefined,
  );
  const { data: uploads } = useUploadsList(decoded || null);
  // Review questions waiting on the operator: the one thing on Status that asks for them.
  const reviewComments = useReviewComments(decoded ? { project_id: decoded } : null).data;
  const reviewsWaiting = new Set(
    (reviewComments?.comments ?? [])
      .filter((c) => c.status === "needs_input")
      .map((c) => c.conversation_id ?? c.comment_id),
  ).size;
  const location = useLocation();
  const roster = useStatefulAgents().data?.agents;
  const tabGroups = withSpecialistTabs(roster ?? []);
  const activeTab = resolveTab(location.pathname, tabGroups);
  const onLiveStatus =
    activeTab.group.id === "status" && !location.pathname.includes("/status-history");
  // Shares the Overview's query, so the strip costs no request of its own.
  // Until it loads (or if it fails) every tab shows, rather than hiding Legal's.
  const agentStatus = useProjectAgentStatus(decoded || null).data;
  const switchedOn = agentStatus
    ? new Set(agentStatus.agents.map((a) => a.agent))
    : null;
  const visibleTabs = activeTab.group.tabs.filter(
    (t) =>
      !t.agent ||
      switchedOn === null ||
      switchedOn.has(t.agent) ||
      t.to === activeTab.tab.to,
  );
  const shownTabs = agentsNotInvolvedLast(
    visibleTabs,
    new Set(
      (agentStatus?.agents ?? []).filter((a) => isStoodDown(a.row)).map((a) => a.agent),
    ),
  );

  // Removed and quarantined rows keep a stale "failed" status, so they're excluded
  // - matching ExtractionSummaryBar + FailedExtractionsPanel, which this badge
  // sends the operator to.
  const failedFileCount = (uploads ?? []).filter(
    (u) =>
      u.extraction_status === "failed" &&
      u.status !== "removed" &&
      u.status !== "quarantined",
  ).length;
  // Keyed by tab so both nav levels render from one definition.
  const badges: Partial<
    // "info" is something to answer, not something broken.
    Record<TabId, { count: number; tooltip: string; info?: boolean }>
  > = {};
  if (failedFileCount > 0) {
    badges.files = {
      count: failedFileCount,
      tooltip: `${failedFileCount} file${failedFileCount === 1 ? "" : "s"} failed extraction. Open Files to see the error and Re-extract (or exclude the file / thread).`,
    };
  }
  if (reviewsWaiting > 0) {
    badges["learning-agents/conversations"] = {
      count: reviewsWaiting,
      tooltip: `${reviewsWaiting} review${reviewsWaiting === 1 ? " is" : "s are"} waiting on your answer. Open Conversations to reply.`,
      info: true,
    };
  }
  // Agent feedback waits on the operator on the specialist's own tab. An action's date
  // move is answered on its Next actions row, which already shows it, so it is not counted.
  for (const a of agentStatus?.agents ?? []) {
    const count = a.feedback_waiting;
    if (count > 0) {
      badges[`status/${a.agent}` as TabId] = {
        count,
        tooltip: `${count} agent question${count === 1 ? " is" : "s are"} waiting on your answer. Open ${a.label} to answer.`,
        info: true,
      };
    }
  }
  const renderBadge = (id: TabId) => {
    const badge = badges[id];
    if (!badge) return null;
    return (
      <Tooltip content={badge.tooltip}>
        <span className={badge.info ? styles.tabBadgeInfo : styles.tabBadge}>{badge.count}</span>
      </Tooltip>
    );
  };

  // A group with one operator-facing tab shows that tab's count (Conversations beside its
  // debug tabs); a group of several shows none, its tabs carry their own once opened.
  const groupBadge = (group: TabGroup) => {
    const shown = group.tabs.filter((t) => !t.debug);
    return shown.length === 1 ? renderBadge(shown[0].to) : null;
  };

  const showSwitcher = slots !== undefined && slots.length > 1;
  const headerActions = (showSwitcher || project?.bma_site_id) && (
    <span className={styles.headerActions}>
      {showSwitcher && <AgentSlotSwitcher slots={slots} slot={slot} onChoose={chooseSlot} />}
      {project?.bma_site_id && (
        <a
          className={styles.editBtn}
          href={adminSiteUrl(project.bma_site_id)}
          target="_blank"
          rel="noreferrer"
        >
          Open in Admin
        </a>
      )}
    </span>
  );

  return (
    <div className={styles.hub}>
      {/* The name and the tabs pin together, so the project stays named as it scrolls. */}
      <div ref={pinnedBand} className={styles.top}>
        <PageHeader
          heading={
            <ProjectTitle
              projectId={decoded}
              title={project ? projectDisplayName(project) : decoded}
              friendlyName={project ? (project.friendly_name ?? null) : undefined}
              titleClassName={pageTitleClassName}
            />
          }
          actions={headerActions}
          description={
            project?.friendly_name &&
            project.name &&
            project.name !== project.friendly_name &&
            (project.bma_site_id ? (
              <a
                className={styles.bmaName}
                href={adminSiteUrl(project.bma_site_id)}
                target="_blank"
                rel="noreferrer"
                title="Open this site on admin"
              >
                {project.name}
              </a>
            ) : (
              <span title="The site's name on admin">{project.name}</span>
            ))
          }
        />
        {/* Active state comes from the resolved tab, not link matching: a group
            links to its FIRST tab. */}
        <TabRows
          rows={[
            {
              ariaLabel: "Project sections",
              items: tabGroups.map((group) => ({
                to: group.tabs[0].to + slotSearch,
                label: group.label,
                active: group.id === activeTab.group.id,
                badge: groupBadge(group),
              })),
            },
            {
              ariaLabel: `${activeTab.group.label} sections`,
              items: shownTabs.map((t) => ({
                to: t.to + slotSearch,
                label: t.label,
                active: t.to === activeTab.tab.to,
                badge: renderBadge(t.to),
                dividerBefore: t.divider,
                debug: t.debug,
              })),
              // Rerun now is the whole team's run over the site, so it sits with the
              // tabs rather than on one agent's page, after when it last ran.
              aside: onLiveStatus ? (
                <span className={styles.statusAside}>
                  <LastRun projectId={decoded} />
                  {(agentStatus?.agents.length ?? 0) > 0 && (
                    <RerunNowButton
                      projectId={decoded}
                      slot={slot}
                      pageLabel={activeTab.tab.label}
                    />
                  )}
                </span>
              ) : undefined,
            },
          ]}
        />
    </div>
      <div className={styles.body}>
        {slotInView && <AgentSlotBanner slot={slotInView} />}
        <Outlet />
      </div>
    </div>
  );
};
